import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, DollarSign, TrendingUp, Clock, FileText } from "lucide-react";
import { reportsService } from "@/services/reportsService";
import { format, startOfYear, endOfYear, eachWeekOfInterval, getWeek, parseISO } from "date-fns";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ReferenceLine } from "recharts";

const categoryColors = {
  Original: "bg-gray-900 text-white",
  Consolidation: "bg-red-500 text-white", 
  Affiliate: "bg-blue-500 text-white",
  "Ad Scores": "bg-orange-500 text-white"
};

const categoryChartColors = {
  Original: "#1f2937",
  Consolidation: "#ef4444", 
  Affiliate: "#3b82f6",
  "Ad Scores": "#f97316"
};

export function WisdropReports() {
  // Fetch analytics data for summary cards (30-day window)
  const { data: analytics, isLoading: isLoadingAnalytics } = useQuery({
    queryKey: ['reports-analytics'],
    queryFn: () => reportsService.getAnalyticsData(),
    staleTime: 0,
    gcTime: 0,
  });

  // Fetch ALL reports for the chart (entire year)
  const { data: allReports, isLoading: isLoadingReports } = useQuery({
    queryKey: ['reports-all'],
    queryFn: () => reportsService.getReports(),
    staleTime: 0,
    gcTime: 0,
  });

  const isLoading = isLoadingAnalytics || isLoadingReports;

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Wisdrop Reports
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">Loading reports analytics...</div>
        </CardContent>
      </Card>
    );
  }

  const completed = analytics?.completed || [];
  const inProgress = analytics?.inProgress || [];
  const planned = analytics?.planned || [];

  const totalCompletedCost = completed.reduce((sum, report) => {
    const externalCosts = report.external_costs as any;
    return sum + (report.internal_costs || 0) + 
           (externalCosts?.panel || 0) + 
           (externalCosts?.analyst || 0) + 
           (externalCosts?.data || 0);
  }, 0);

  const totalInProgressCost = inProgress.reduce((sum, report) => {
    const externalCosts = report.external_costs as any;
    return sum + (report.internal_costs || 0) + 
           (externalCosts?.panel || 0) + 
           (externalCosts?.analyst || 0) + 
           (externalCosts?.data || 0);
  }, 0);

  // Generate timeline data for all weeks of the year (1-52) using ALL reports
  const currentYear = new Date().getFullYear();
  
  // Filter all reports for the current year
  const reportsThisYear = (allReports || []).filter(report => {
    const dateStr = report.delivery_date || report.start_date || report.created_at;
    if (!dateStr) return false;
    const reportDate = parseISO(dateStr);
    return reportDate.getFullYear() === currentYear;
  });
  
  // Get current week number (using ISO week date system with Monday as first day)
  const currentWeekNumber = getWeek(new Date(), { weekStartsOn: 1, firstWeekContainsDate: 4 });

  // Create timeline data for weeks 1-52
  const timelineData = Array.from({ length: 52 }, (_, index) => {
    const weekNumber = index + 1;
    
    // Find reports for this week based on delivery date, start_date, or created_at
    const weekReports = reportsThisYear.filter(report => {
      const dateStr = report.delivery_date || report.start_date || report.created_at;
      if (!dateStr) return false;
      const reportDate = parseISO(dateStr);
      return getWeek(reportDate, { weekStartsOn: 1, firstWeekContainsDate: 4 }) === weekNumber;
    });

    const categoryData = {
      Original: 0,
      Consolidation: 0,
      Affiliate: 0,
      "Ad Scores": 0
    };

    weekReports.forEach(report => {
      if (categoryData.hasOwnProperty(report.category)) {
        categoryData[report.category]++;
      }
    });

    return {
      week: weekNumber,
      total: weekReports.length,
      ...categoryData,
      reports: weekReports
    };
  });

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      
      return (
        <div className="bg-background border border-border rounded-lg p-3 shadow-lg max-w-sm z-50">
          <p className="font-medium mb-2">Week {label}</p>
          
          {/* Show reports with color coding */}
          {data.reports && data.reports.length > 0 ? (
            <div className="space-y-1">
              {data.reports.map((report: any) => (
                <div 
                  key={report.id} 
                  className="text-sm font-medium"
                  style={{ color: categoryChartColors[report.category] }}
                >
                  {report.name} S{report.season}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No reports this week</p>
          )}
          
          {data.total > 0 && (
            <>
              <hr className="my-2" />
              <p className="text-sm font-bold">
                Total: {data.total} reports
              </p>
            </>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Wisdrop Reports per Week
            </CardTitle>
          </div>
        </div>
        
        {/* Summary Cards integrated in header */}
        <div className="grid gap-4 md:grid-cols-3 pt-4">
          <div className="text-center p-3 bg-muted/30 rounded-lg">
            <div className="flex items-center justify-center mb-2">
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="text-2xl font-bold">{completed.length}</div>
            <div className="text-sm text-muted-foreground">Completed Past 30 Days</div>
            <div className="text-xs text-muted-foreground">
              Total cost: €{totalCompletedCost.toLocaleString()}
            </div>
            <div className="flex flex-wrap gap-1 mt-2 justify-center">
              {completed.slice(0, 2).map((report) => (
                <Badge 
                  key={report.id}
                  variant="outline" 
                  className={`text-xs ${categoryColors[report.category]}`}
                >
                  {report.name} S{report.season}
                </Badge>
              ))}
              {completed.length > 2 && (
                <Badge variant="outline" className="text-xs">
                  +{completed.length - 2}
                </Badge>
              )}
            </div>
          </div>
          
          <div className="text-center p-3 bg-muted/30 rounded-lg">
            <div className="flex items-center justify-center mb-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="text-2xl font-bold">{inProgress.length}</div>
            <div className="text-sm text-muted-foreground">In Progress</div>
            <div className="text-xs text-muted-foreground">
              Current cost: €{totalInProgressCost.toLocaleString()}
            </div>
            <div className="flex flex-wrap gap-1 mt-2 justify-center">
              {inProgress.slice(0, 2).map((report) => (
                <Badge 
                  key={report.id}
                  variant="outline" 
                  className={`text-xs ${categoryColors[report.category]}`}
                >
                  {report.name} S{report.season}
                </Badge>
              ))}
              {inProgress.length > 2 && (
                <Badge variant="outline" className="text-xs">
                  +{inProgress.length - 2}
                </Badge>
              )}
            </div>
          </div>
          
          <div className="text-center p-3 bg-muted/30 rounded-lg">
            <div className="flex items-center justify-center mb-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="text-2xl font-bold">{planned.length}</div>
            <div className="text-sm text-muted-foreground">Planned Next 30 Days</div>
            <div className="text-xs text-muted-foreground">
              Starting soon
            </div>
            <div className="flex flex-wrap gap-1 mt-2 justify-center">
              {planned.slice(0, 2).map((report) => (
                <div key={report.id} className="text-center">
                  <Badge 
                    variant="outline" 
                    className={`text-xs ${categoryColors[report.category]}`}
                  >
                    {report.name} S{report.season}
                  </Badge>
                  {report.start_date && (
                    <div className="text-xs text-muted-foreground mt-1">
                      {format(new Date(report.start_date), 'MMM dd')}
                    </div>
                  )}
                </div>
              ))}
              {planned.length > 2 && (
                <Badge variant="outline" className="text-xs">
                  +{planned.length - 2}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        <div className="space-y-4">
          
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={timelineData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis 
                  dataKey="week" 
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis 
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                  domain={[0, 5]}
                  tickCount={6}
                  tickFormatter={(value) => value.toString()}
                />
                <Tooltip content={<CustomTooltip />} />
                
                {/* Reference line for current week */}
                <ReferenceLine 
                  x={currentWeekNumber} 
                  stroke="hsl(var(--destructive))" 
                  strokeDasharray="5 5"
                  strokeWidth={2}
                  label={{ 
                    value: "Today", 
                    position: "insideTopRight",
                    offset: 15,
                    style: {
                      textAnchor: 'middle',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      fill: 'hsl(var(--destructive))',
                      backgroundColor: 'white',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      border: '1px solid hsl(var(--destructive))'
                    }
                  }}
                />
                
                <Bar dataKey="Original" stackId="reports" fill={categoryChartColors.Original} />
                <Bar dataKey="Consolidation" stackId="reports" fill={categoryChartColors.Consolidation} />
                <Bar dataKey="Affiliate" stackId="reports" fill={categoryChartColors.Affiliate} />
                <Bar dataKey="Ad Scores" stackId="reports" fill={categoryChartColors["Ad Scores"]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          
          {/* Legend */}
          <div className="flex justify-center gap-6">
            {Object.entries(categoryChartColors).map(([category, color]) => (
              <div key={category} className="flex items-center gap-2">
                <div 
                  className="w-4 h-4 rounded" 
                  style={{ backgroundColor: color }}
                />
                <span className="text-sm font-medium">{category}</span>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}